import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Menu,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
} from "@mui/material";
import { filterMenuSlotProps } from "./filterMenuStyles.js";
import { useThemeContext } from "../context/themeContext.js";

// Toggle order; the first is also where an unset filter opens
const MODE_LABELS = { years: "Years", range: "Range" };

// The release-year filter's URL token (see _apply_year_filter in filters.py)
// is a comma-separated list of items, each a year ("1999") or an inclusive
// range ("1990..1999", either end optional). A whole decade is just a range.
const decadeItem = (decade) => `${decade}..${decade + 9}`;
const isDecadeItem = (item) => {
  const [from, to] = item.split("..").map(Number);
  return item.includes("..") && from % 10 === 0 && to === from + 9;
};
const decadeOf = (year) => Math.floor(year / 10) * 10;

/** Chip text for one item: "1999", "1990s", "1985–2005", "From 1990", "Until 1899" */
const formatItem = (item) => {
  if (!item.includes("..")) return item;
  if (isDecadeItem(item)) return `${item.slice(0, 4)}s`;
  const [from, to] = item.split("..");
  if (from && to) return `${from}–${to}`;
  return from ? `From ${from}` : `Until ${to}`;
};

/** What the filter's chip says: its first item, plus how many more */
export const formatReleaseYearFilter = (token) => {
  const items = token.split(",");
  const first = formatItem(items[0]);
  return items.length > 1 ? `${first} +${items.length - 1}` : first;
};

/**
 * Starting draft from the URL token. A lone range that isn't a decade is a
 * custom range; anything else is years and decades picked from the grid.
 */
const parseReleaseYearFilter = (token) => {
  const draft = { mode: "years", items: [], from: "", to: "" };
  if (!token) return draft;
  const items = token.split(",");
  if (
    items.length === 1 &&
    items[0].includes("..") &&
    !isDecadeItem(items[0])
  ) {
    const [from, to] = items[0].split("..");
    return { ...draft, mode: "range", from, to };
  }
  return { ...draft, items };
};

/**
 * Years as a grid of chips under decade headings, newest first. A heading is
 * itself selectable, picking the whole decade (every year in it, not only
 * those in the library); its years then show as selected through it. Tapping
 * a year inside a selected decade swaps the decade for its other years.
 *
 * @param {number[]} years - Years to show, newest first
 * @param {string[]} items - Selected items (years and decade ranges)
 */
const YearChipGrid = ({ years, items, onChange }) => {
  const byDecade = years.reduce((groups, year) => {
    (groups[decadeOf(year)] ??= []).push(year);
    return groups;
  }, {});
  const decades = Object.keys(byDecade)
    .map(Number)
    .sort((a, b) => b - a);

  const toggleDecade = (decade) => {
    const item = decadeItem(decade);
    // Selecting a decade absorbs any of its years picked one by one
    const others = items.filter(
      (i) => i !== item && (i.includes("..") || decadeOf(Number(i)) !== decade),
    );
    onChange(items.includes(item) ? others : [...others, item]);
  };

  const toggleYear = (year) => {
    const item = decadeItem(decadeOf(year));
    if (items.includes(item)) {
      onChange([
        ...items.filter((i) => i !== item),
        ...byDecade[decadeOf(year)].filter((y) => y !== year).map(String),
      ]);
    } else if (items.includes(String(year))) {
      onChange(items.filter((i) => i !== String(year)));
    } else {
      onChange([...items, String(year)]);
    }
  };

  const chipProps = (isSelected) => ({
    size: "small",
    color: isSelected ? "primary" : "default",
    variant: isSelected ? "filled" : "outlined",
  });

  return (
    <Stack spacing={1.5} sx={{ maxHeight: 260, overflowY: "auto" }}>
      {decades.map((decade) => {
        const decadeSelected = items.includes(decadeItem(decade));
        return (
          <Box key={decade}>
            <Chip
              label={`${decade}s`}
              {...chipProps(decadeSelected)}
              onClick={() => toggleDecade(decade)}
              sx={{ mb: 1 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1,
                // Indented under its decade chip, to read as belonging to it
                pl: 2,
              }}
            >
              {byDecade[decade].map((year) => (
                <Chip
                  key={year}
                  label={year}
                  {...chipProps(decadeSelected || items.includes(String(year)))}
                  onClick={() => toggleYear(year)}
                />
              ))}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

/**
 * Track's release-year filter: pick years and whole decades from a grid, or
 * a custom range of years (either end optional). Nothing applies until Apply.
 *
 * @param {string|null} value - The filter's current URL token, if set
 * @param {number[]} yearOptions - Release years present in the library
 * @param {boolean} loading - Whether yearOptions are still being fetched
 * @param {(token: string) => void} onApply
 * @param {() => void} onClear
 */
const ReleaseYearFilterMenu = ({
  value,
  yearOptions,
  loading,
  onApply,
  onClear,
  anchorEl,
  onClose,
}) => {
  // Mounted fresh each time the menu opens, so this starts from the URL
  const [draft, setDraft] = useState(() => parseReleaseYearFilter(value));
  // Touch devices get the native select (the phone's own picker suits a long
  // list of years); mouse users get MUI's, to match the rest of the app
  const { isTouchDevice } = useThemeContext();

  const isBackwards =
    draft.from && draft.to && Number(draft.from) > Number(draft.to);
  const canApply =
    draft.mode === "years"
      ? draft.items.length > 0
      : (draft.from || draft.to) && !isBackwards;

  const handleApply = () => {
    if (draft.mode === "range") {
      onApply(`${draft.from}..${draft.to}`);
      return;
    }
    // Newest first, so the chip leads with the most recent pick
    const latestYear = (item) => Number(item.split("..").pop());
    onApply(
      [...draft.items].sort((a, b) => latestYear(b) - latestYear(a)).join(","),
    );
  };

  const yearsUnavailable = loading ? (
    <Stack spacing={1}>
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} variant="text" width={120} />
      ))}
    </Stack>
  ) : yearOptions.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      Nothing to filter by yet
    </Typography>
  ) : null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      slotProps={filterMenuSlotProps}
    >
      {/* One Stack, so every section sits the same distance apart */}
      <Stack spacing={2} sx={{ px: 2, py: 1.5, width: 280 }}>
        <ToggleButtonGroup
          value={draft.mode}
          exclusive
          size="small"
          fullWidth
          // Deselecting the active mode reports null — keep it selected
          onChange={(event, mode) => mode && setDraft({ ...draft, mode })}
        >
          {Object.entries(MODE_LABELS).map(([mode, label]) => (
            <ToggleButton
              key={mode}
              value={mode}
              sx={{ textTransform: "none" }}
            >
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {yearsUnavailable ??
          (draft.mode === "years" ? (
            <YearChipGrid
              years={yearOptions}
              items={draft.items}
              onChange={(items) => setDraft({ ...draft, items })}
            />
          ) : (
            <Stack direction="row" spacing={1}>
              {[
                ["from", "From"],
                ["to", "To"],
              ].map(([key, label]) => (
                <TextField
                  key={key}
                  select
                  label={label}
                  size="small"
                  value={draft[key]}
                  onChange={(event) =>
                    setDraft({ ...draft, [key]: event.target.value })
                  }
                  error={!!isBackwards}
                  SelectProps={
                    isTouchDevice
                      ? { native: true }
                      : {
                          // Shows "Any" while nothing is picked
                          displayEmpty: true,
                          MenuProps: { PaperProps: { sx: { maxHeight: 300 } } },
                        }
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                >
                  {/* String values, matching the draft's (parsed from the
                      URL) — MUI's select compares them strictly */}
                  {[["", "Any"], ...yearOptions.map((y) => [String(y), y])].map(
                    ([optionValue, optionLabel]) =>
                      isTouchDevice ? (
                        <option key={optionValue} value={optionValue}>
                          {optionLabel}
                        </option>
                      ) : (
                        <MenuItem key={optionValue} value={optionValue} dense>
                          {optionLabel}
                        </MenuItem>
                      ),
                  )}
                </TextField>
              ))}
            </Stack>
          ))}

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button variant="outlined" size="small" onClick={onClear}>
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={!canApply}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Stack>
      </Stack>
    </Menu>
  );
};

export default ReleaseYearFilterMenu;
